import React from "react";
import { ContextMenu, ContextMenuTrigger } from "@/components/ui/context-menu";
import { RowActions } from "./RowActions";
import { RowContextMenuContent, type ActiveCell } from "./RowContextMenuContent";
import { MemoizedTableCell } from "./TableCell";
import { useSimpleTableContext } from "../../contexts/SimpleTableContext";
import { useFilterContext } from "@/contexts/FilterContext";
import type { BaseEntity, SimpleTableConfig, UniversalInlineEditHandler } from "../../types";
import type { AuthenticatedUser } from "@/lib/types/unified";

export interface TableRowProps<TData extends BaseEntity> {
  item: TData;
  index: number;
  textareaFieldSet: Set<string>;
  rowStyle: React.CSSProperties;
  dataRowIds?: string[];
  dataIndex?: number;
}

export function TableRow<TData extends BaseEntity>({
  item,
  index,
  textareaFieldSet,
  rowStyle,
  dataRowIds,
  dataIndex,
}: TableRowProps<TData>) {
  // Get context for all table state and handlers
  const context = useSimpleTableContext<TData>();

  const {
    config,
    visibleFields,
    state,
    callbacks,
    auth,
    hooks,
    utils,
    onEdit,
    onView,
    onDelete,
    onInlineEdit,
    onOpenDetailModal,
    data,
  } = context;

  const hasRowContextMenu =
    hooks &&
    config.detailActions &&
    config.detailActions.length > 0;
  const isRowEditing = state.editingCell?.rowId === item.id;

  // Cell-aware context menu: on right-click we record which cell was targeted
  // (via the data-field-* attributes TableCellWrapper emits) so the row menu can
  // offer copy / filter-by-value for that specific cell without every cell
  // needing its own nested menu.
  const filterContext = useFilterContext<TData>();
  const [activeCell, setActiveCell] = React.useState<ActiveCell | null>(null);

  const captureCell = React.useCallback((e: React.MouseEvent) => {
    const el = (e.target as HTMLElement).closest<HTMLElement>("[data-field-id]");
    if (!el || !el.dataset.fieldId) {
      setActiveCell(null);
      return;
    }
    const text = el.textContent?.trim();
    setActiveCell({
      fieldId: el.dataset.fieldId,
      label: el.dataset.fieldLabel || el.dataset.fieldId,
      filterValue: el.dataset.filterValue,
      copyText: text ? text : undefined,
    });
  }, []);

  const handleCopyCellValue = React.useCallback((text: string) => {
    void navigator.clipboard?.writeText(text);
  }, []);

  const handleFilterByCellValue = React.useCallback(
    (fieldId: string, value: string) => {
      filterContext.actions.setSimpleFilter(fieldId, value);
    },
    [filterContext]
  );

  const canFilterByCell = config.features?.filters === true;

  // Handler for row click to open detail modal
  const handleRowClick = React.useCallback(
    (e: React.MouseEvent) => {
      // Don't open modal if:
      // - Clicking on checkbox
      // - Clicking on action buttons
      // - Clicking on a link
      // - Currently editing a cell
      const target = e.target as HTMLElement;
      if (
        target.closest("input[type=\"checkbox\"]") ||
        target.closest("button") ||
        target.closest("a") ||
        target.closest("[data-no-row-click]") ||
        isRowEditing
      ) {
        return;
      }

      // Open detail modal if handler is provided
      if (onOpenDetailModal && data) {
        const itemIds = data.map((d: TData) => d.id);
        const itemIndex = itemIds.indexOf(item.id);
        onOpenDetailModal(item, itemIds, itemIndex);
      } else if (onView) {
        // Fallback to onView if onOpenDetailModal is not provided
        onView(item);
      }
    },
    [item, data, onOpenDetailModal, onView, isRowEditing]
  );

  const rowContent = (
    <>
      {/* Selection - sticky so the checkbox stays in view during horizontal scroll */}
      <div className="w-10 h-full flex items-center justify-center cursor-pointer transition-colors sticky left-0 z-raised bg-inherit border-r border-border">
        <input
          type="checkbox"
          checked={state.selectedItems.has(item.id)}
          onChange={(e) => callbacks.handleSelectItem(item.id, e.target.checked)}
          className="w-5 h-5 rounded border-gray-300 dark:border-gray-700 text-blue-600 focus:ring-blue-500 cursor-pointer"
        />
      </div>

      {/* Data Cells */}
      {visibleFields.map(([fieldId, fieldConfig], cellIndex) => {
        const isTextareaField = textareaFieldSet.has(fieldId);

        return (
          <MemoizedTableCell
            key={fieldId}
            fieldId={fieldId}
            fieldConfig={fieldConfig}
            item={item}
            cellIndex={cellIndex}
            totalCells={visibleFields.length}
            isTextareaField={isTextareaField}
            dataRowIds={dataRowIds}
            dataIndex={dataIndex}
          />
        );
      })}

      {/* Trailing Actions Column - Only show if actions are not inline with title */}
      {config.rowActionsPlacement !== "inlineTitle" && (
        <div className="w-16 px-2 py-1 flex items-center justify-center flex-shrink-0">
          <RowActions
            item={item}
            config={config}
            hooks={hooks}
            onEdit={onEdit}
            onView={onView}
            onDelete={onDelete}
            compact={true}
          />
        </div>
      )}
    </>
  );

  const rowClassName = `relative flex items-center min-h-[36px] max-h-24 cursor-pointer ${
    isRowEditing ? "overflow-visible z-[200]" : "overflow-hidden"
  } bg-background hover:bg-muted/45 transition-colors${
    (dataIndex ?? 0) % 2 === 0 ? " bg-muted/25" : ""
  }`;

  if (hasRowContextMenu) {
    return (
      <ContextMenu>
        <ContextMenuTrigger asChild>
          <div
            className={rowClassName}
            style={rowStyle}
            onClick={handleRowClick}
            onContextMenuCapture={captureCell}
          >
            {rowContent}
          </div>
        </ContextMenuTrigger>
        <RowContextMenuContent
          item={item}
          actions={config.detailActions ?? []}
          hooks={hooks}
          entityName={config.entity}
          config={config}
          activeCell={activeCell}
          onCopyCellValue={handleCopyCellValue}
          onFilterByCellValue={canFilterByCell ? handleFilterByCellValue : undefined}
        />
      </ContextMenu>
    );
  }

  return (
    <div className={rowClassName} style={rowStyle} onClick={handleRowClick}>
      {rowContent}
    </div>
  );
}

// Memoized wrapper for performance
export const MemoizedTableRow = React.memo(TableRow) as typeof TableRow;
